import * as THREE from 'three';
import { IWall } from '../models/abstract/Wall';
import { areLineSegmentsIntersecting, movePointBAlongLine } from './vectorUtils';

const COMPLETION_POINTS_DISTANCE_TOLERANCE = 3.5;
const PARALLEL_DISTANCE_TOLERANCE = 2;
const WALL_THRESHOLD = 2.5;

// Formulars and code copied from: https://algs4.cs.princeton.edu/91primitives/

/**
 * Function to determine whether a polygon contains intersections.
 * @param polygon - The polygon as a Shape structure.
 * @returns {boolean} - A flag indicating whether the polygon contains self-intersecting walls.
 */
export const hasIntersections = (polygon: THREE.Shape): boolean => {
  // Flag to track if intersections are found
  let result = false;

  // Iterate through each curve in the polygon
  for (let i = 0; i < polygon.curves.length; i++) {
    const actualCurve = polygon.curves[i] as THREE.LineCurve;
    const startPointCurveA = actualCurve.v1;
    const endPointCurveA = actualCurve.v2;

    // Calculation of the direction of curve a
    const directionCurveA = new THREE.Vector2().subVectors(endPointCurveA, startPointCurveA);

    // Compare with every other curve in the polygon
    for (let j = i + 1; j < polygon.curves.length; j++) {
      // Ignore consecutive curves and wrap-around connections
      if (Math.abs(i - j) <= 1 || (i === 0 && j === polygon.curves.length - 1)) {
        continue;
      }

      const nextCurve = polygon.curves[j] as THREE.LineCurve;
      const startPointCurveB = nextCurve.v1;
      const endPointCurveB = nextCurve.v2;

      // Calculation of the direction of curve a
      const directionCurveB = new THREE.Vector2().subVectors(endPointCurveB, startPointCurveB);

      // Calculating cross product of the two curves
      const crossProduct = directionCurveA.x * directionCurveB.y - directionCurveA.y * directionCurveB.x;

      // Special case: The two lines are parallel
      if (crossProduct === 0) {
        const distanceBetweenLines = startPointCurveA.distanceTo(endPointCurveB);
        if (distanceBetweenLines < PARALLEL_DISTANCE_TOLERANCE) {
          result = true;
          break;
        }
      }

      // Check if these two segments intersect
      if (areLineSegmentsIntersecting(startPointCurveA, endPointCurveA, startPointCurveB, endPointCurveB)) {
        result = true;
        break;
      }
    }

    if (result) break; // Exit early if an intersection is found
  }

  return result;
};

/**
 * Function to determine whether a set of walls forms intersections.
 * @param walls - The collection of walls.
 * @returns {boolean} - A flag indicating whether the walls form intersections.
 */
export const hasWallIntersections = (walls: Array<IWall>): boolean => {
  // Flag to track if intersections are found
  let result = false;

  // Assuming walls are arranged clockwise
  // Tacking the first starting point for a potential completion
  const roomStartingPoint = walls[0].startPoint;

  // Iterate through each curve in the polygon
  for (let i = 0; i < walls.length; i++) {
    const wall = walls[i];
    const startPoint = wall.startPoint;
    const endPoint = wall.endPoint;

    // Compare with every other curve in the polygon
    for (let j = i + 1; j < walls.length; j++) {
      // Ignore the neighbor wall
      if (j === i + 1) {
        continue;
      }

      const nextWall = walls[j];
      const startPointWallB = nextWall.startPoint;
      const endPointWallB = nextWall.endPoint;

      // Calculating the distance between the potential endpoint and the start of the first wall (potential room completion)
      const distanceToStartingPoint = roomStartingPoint.distanceTo(endPointWallB);

      // Avoiding false positives in case the drawing should be completed
      if (distanceToStartingPoint < COMPLETION_POINTS_DISTANCE_TOLERANCE) {
        break;
      }

      const extendedEndPoint = movePointBAlongLine(startPoint, endPoint, WALL_THRESHOLD);
      // Check if these two segments intersect
      if (areLineSegmentsIntersecting(startPoint, extendedEndPoint, startPointWallB, endPointWallB)) {
        result = true;
        break;
      }
    }
    if (result) break; // Exit early if an intersection is found
  }

  return result;
};

